export enum CellState {
  Empty = 'empty',
  Filled = 'filled',
  Marked = 'marked',
}

export type Difficulty = number; // 0-100 difficulty score

export type DensityPreset = 'sparse' | 'normal' | 'dense';

export type Category =
  | 'retro'
  | 'tech'
  | 'media'
  | 'controls'
  | 'communication'
  | 'buildings'
  | 'nature'
  | 'people'
  | 'objects'
  | 'tools'
  | 'files'
  | 'transport'
  | 'symbols'
  | 'random'
  | 'community';

/** Sub-puzzle bounds within a level's solution grid. */
export interface PuzzleDefinition {
  id: string;
  row: number;
  col: number;
  width: number;
  height: number;
  difficulty: Difficulty;
}

/** A level with a full solution image and positioned sub-puzzles. */
export interface LevelData {
  id: string;
  name: string;
  category: Category;
  solution: boolean[][];
  puzzles: PuzzleDefinition[];
}

/** A resolved sub-puzzle with its solution slice — ready to play. */
export interface PuzzleData {
  width: number;
  height: number;
  difficulty: Difficulty;
  solution: boolean[][];
}

export type PuzzleEntry = 'fresh' | 'resume' | 'view';

export type LevelTrack = 'progression' | 'daily';

export type Screen =
  | { type: 'home' }
  | { type: 'tutorial'; lesson?: number }
  | { type: 'levels-menu' }
  | { type: 'lab-setup' }
  | { type: 'level'; mode: 'levels'; puzzleIndex: number; entry: PuzzleEntry }
  | {
      type: 'level';
      mode: 'multi-sub';
      track: LevelTrack;
      difficulty: PuzzleDifficulty;
      levelIndex: number;
      subIndex: number;
      entry: PuzzleEntry;
    }
  | {
      type: 'level';
      mode: 'train';
      size: number;
      seed: TrainSeed;
      density: DensityPreset;
      strict: boolean;
      hintCount: CountSetting;
      validateCount: CountSetting;
      autoMark: boolean;
      entry: PuzzleEntry;
    }
  | { type: 'play'; search: string }
  | { type: 'admin' }
  | { type: 'design' }
  | { type: 'admin-card-designer' }
  | { type: 'admin-state-editor' }
  | { type: 'admin-grid-tester' }
  | { type: 'admin-levels'; track: LevelTrack }
  | { type: 'creator' }
  | { type: 'creator-editor' }
  | { type: 'setup' }
  | { type: 'history' };

export interface TutorialAction {
  row: number;
  col: number;
  type: 'fill' | 'mark';
}

export type TutorialStepAction =
  | { type: 'next' }
  | { type: 'tap'; actions: TutorialAction[] }
  | { type: 'free' };

export interface TutorialStepData {
  text?: string;
  heading?: string;
  action: TutorialStepAction;
  /** Cells to highlight with a golden pulse. */
  highlightCells?: { row: number; col: number }[];
  /** Custom error messages for specific cells. */
  errorOverrides?: { row: number; col: number; message: string; color?: string }[];
}

export interface TutorialLessonData {
  title: string;
  size: number;
  solution: boolean[][];
  preset?: TutorialAction[];
  /** Always show the fill/mark mode toggle for this lesson. */
  showModeSwitcher?: boolean;
  steps: TutorialStepData[];
}

export function getStars(
  width: number,
  height: number,
  seconds: number
): number {
  const dim = Math.max(width, height);
  if (dim <= 5) return seconds < 60 ? 3 : seconds < 120 ? 2 : 1;
  if (dim <= 10) return seconds < 180 ? 3 : seconds < 360 ? 2 : 1;
  return seconds < 300 ? 3 : seconds < 600 ? 2 : 1;
}

export function formatTime(s: number): string {
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

// --- Persistence types (previously in storage.ts / LabScreen.tsx) ---

export interface Completion {
  time: number;
  completedAt: number;
  hints?: number;
}

export interface SavedGame {
  grid: string[][];
  elapsed: number;
  updatedAt?: number;
}

export type HistoryCategory = 'progression' | 'daily' | 'shared' | 'train';

/** A completed puzzle/level entry shown on the History screen.
 *  Each entry is self-contained — its `solution` is stored inline so the
 *  history can render thumbnails without re-looking-up level data. */
export interface HistoryRecord {
  /** Stable dedupe id (replays of the same puzzle replace the entry).
   *  e.g. `campaign:<levelId>`, `daily:<levelId>`, `shared:<slug>`,
   *  `train:<storageKey>`. */
  id: string;
  /** Original storage key — used to clear saved grid state on replay. */
  storageKey: string;
  /** Display label for the row ("Level 5", "Daily · Moon", etc.). */
  label: string;
  category: HistoryCategory;
  /** Full solution grid (rows × cols of booleans) for thumbnail rendering. */
  solution: boolean[][];
  /** Completion time in ms since epoch. */
  timestamp: number;
  /** Total solve time in seconds. */
  seconds: number;
  /** Hints used during the solve. */
  hints: number;
  /** Screen payload to navigate back to (in view mode) via "Open". */
  screen: Screen;
}

export interface TutorialProgress {
  lessonsComplete: boolean[];
  puzzlesComplete: boolean[];
}

export type ConsentStatus = 'granted' | 'denied';

export type LevelCardState = 'locked' | 'in-progress' | 'completed';

export type LevelCardEffect = 'glow';

export type TrainSeed = string;

/** Count setting: 0 = disabled, -1 = unlimited, positive = limited count */
export type CountSetting = number;

export type PuzzleDifficulty = 'casual' | 'advanced';

export interface TrainSettings {
  size: number;
  density: DensityPreset;
  seed: TrainSeed;
  strict: boolean;
  /** Hint count: 0 = disabled, -1 = unlimited, positive = limited */
  hintCount: CountSetting;
  autoMark: boolean;
  /** Validate count: 0 = disabled, -1 = unlimited, positive = limited */
  validateCount: CountSetting;
  difficulty: PuzzleDifficulty;
}
